import React, { useEffect, useRef, useState } from 'react';

/**
 * Bottom Sheet Component
 * Mobile-optimized modal that slides up from bottom with expandable height
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether bottom sheet is visible
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Sheet title
 * @param {React.ReactNode} props.children - Sheet content
 * @param {number} props.initialHeight - Initial height as percentage (default: 65)
 * @param {number} props.expandedHeight - Expanded height as percentage (default: 90)
 * @param {boolean} props.showHandle - Show drag handle (default: true)
 */
export default function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  initialHeight = 65,
  expandedHeight = 90,
  showHandle = true
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef(null);

  // Reset expansion when closing
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Touch event handlers for swipe gestures
  const handleTouchStart = (  _e) => {
    setStartY(_e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (  _e) => {
    if (!isDragging) {return;}
    setCurrentY(_e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) {return;}

    const deltaY = currentY - startY;
    const threshold = 50; // Minimum swipe distance in pixels

    if (deltaY > threshold) {
      // Swipe down
      if (isExpanded) {
        // Collapse to initial height
        setIsExpanded(false);
      } else {
        // Close the sheet
        onClose();
      }
    } else if (deltaY < -threshold) {
      // Swipe up - expand to full height
      if (!isExpanded) {
        setIsExpanded(true);
      }
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const currentHeight = isExpanded ? expandedHeight : initialHeight;

  if (!isOpen) {return null;}

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-50 md:hidden transition-all duration-300 ease-out"
        style={{
          height: `${currentHeight}vh`,
          transform: isDragging ? `translateY(${Math.max(0, currentY - startY)}px)` : 'translateY(0)'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bottom-sheet-title"
      >
        {/* Drag Handle */}
        {showHandle && (
          <div
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-3 border-b border-gray-200">
          <h2 id="bottom-sheet-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4" style={{ height: 'calc(100% - 80px)' }}>
          {children}
        </div>
      </div>
    </>
  );
}
